import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Signed in");
            nav(loc.state?.from?.pathname || "/app");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            {/* Left visual */}
            <div className="hidden lg:flex bg-card grain-bg border-r border-border relative flex-col justify-between p-12">
                <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2">
                    <Logo className="h-24 w-auto" />
                </Link>
                <div>
                    <div className="overline mb-3">// welcome back</div>
                    <div className="font-display text-4xl tracking-tight leading-tight">
                        The moment you stop guessing<br />is the moment rankings compound.
                    </div>
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                    <div>signal.latency <span className="text-primary">18ms</span></div>
                    <div>uptime <span className="text-primary">99.98%</span></div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center px-6 py-12">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="overline mb-3">Sign in</div>
                    <h1 className="font-display text-3xl tracking-tight mb-8">Enter the forge.</h1>

                    <label className="text-xs text-muted-foreground block mb-2">Email</label>
                    <input
                        data-testid="login-email-input"
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="you@company.com"
                    />
                    <label className="text-xs text-muted-foreground block mt-5 mb-2">Password</label>
                    <input
                        data-testid="login-password-input"
                        type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="••••••••"
                    />
                    <div className="mt-2 text-right">
                        <Link
                            to="/forgot-password"
                            data-testid="forgot-password-link"
                            className="text-xs text-muted-foreground hover:text-primary hover:underline"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    <button
                        data-testid="login-submit-btn"
                        disabled={loading}
                        className="w-full mt-8 bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                        {loading ? "Signing in…" : "Sign in →"}
                    </button>
                    <GoogleSignInButton label="Sign in with Google" />
                    <div className="mt-6 text-xs text-muted-foreground">
                        No account? <Link to="/register" data-testid="switch-register-link" className="text-primary hover:underline">Create one</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
